// The run as the viewer reads it: the reader's columns plus what every component needs
// derived once -- which unit each bundle used (a bitmask), each stream's cycle period,
// each wait's and flag write's key, each DMA's endpoints, which SparseCores showed up
// at all, the span the cores were actually running, and the chip's place in the slice.
//
// Time here is the simulator's virtual picoseconds (symtpu docs/trace.md, docs/timing.md):
// every core and SparseCore stream counts its own cycles, joins another thread's clock
// whenever a sync flag releases one of its waits, and joins the firmware's clock at its
// dispatch. The firmware is placed forward by the wall clock between two anchors, so a
// run's axis includes the host's real gaps and is much longer than the cores' work;
// `span` is where the cores were busy, `focus` the busiest cluster of it.

import { UNIT_BIT, unitOfSlot } from "./isa.js";
import { flagIdOf } from "./fmt.js";
import { lowerIndex, upperIndex } from "./dom.js";
import { toRecords, decodeStrings } from "./pack.js";

export const GENS = ["v5e", "v5p", "v7x"];
export const TC_PER_CHIP = { v5e: 1, v5p: 2, v7x: 2 };
export const SC_PER_CHIP = { v5e: 0, v5p: 4, v7x: 4 };
export const TILES_PER_SC = 16;
// The playground's menu (playground/topology.py): the shapes a chip count maps to.
export const MENU = [
  ["v5e", [1, 1, 1]], ["v5e", [2, 2, 1]], ["v5e", [2, 4, 1]],
  ["v5p", [1, 1, 1]], ["v5p", [2, 2, 1]], ["v7x", [1, 1, 1]], ["v7x", [2, 2, 1]],
];
const TC_HZ = { v5e: 1.5e9, v5p: 1.75e9, v7x: 1.9e9 };
const SC_HZ = { v5p: 1.475e9, v7x: 1.75e9 };

export const UNIT_EV_BIT = { mxu: UNIT_BIT.mxu, xlu: UNIT_BIT.xlu, eup: UNIT_BIT.eup, prng: UNIT_BIT.prng };

function popcount16(v) { let c = 0; for (; v; v >>>= 1) c += v & 1; return c; }

export function parseBy(by) {
  if (by === "Host") return { kind: "host", ci: null };
  const m = /^(TensorCore|SparseCore)\((\d+)\)$/.exec(by || "");
  if (!m) return { kind: "other", ci: null };
  return { kind: m[1] === "TensorCore" ? "tc" : "sc", ci: +m[2] };
}

export class Model {
  constructor(data, opts = {}) {
    // Waits, flag writes, DMAs and the rest arrive as columns (viewer/js/pack.js) and
    // become records here, on the page, once: they are what everything below reads.
    this.data = data = toRecords(data);
    this.meta = data.meta;
    this.strings = data.strings;
    this.timing = !!this.meta.timing;
    this.truncated = !!this.meta.truncated;
    this.tEnd = this.meta.t_end_ps || 0;
    this.chipsSeen = (this.meta.chips_seen || []).slice();
    this.summary = data.summary;
    // Format 2 traces place their own firmware commands in virtual time.
    this.hostTimed = data.host.some((h) => h.ev === "fw_cmd" && Number.isInteger(h.t) && h.t > 0);

    // Bits per interned slot-name string, built as strings are met.
    this.slotBit = new Uint16Array(this.strings.length);
    this.cores = data.cores.map((c, idx) => this.buildCore(c, idx));
    this.coreByKey = new Map(this.cores.map((c) => [c.key, c]));
    this.hasUnits = this.cores.some((c) => c.units.length > 0);

    this.setTopology(opts.gen || this.meta.gen, opts.shape || this.meta.topology);
    for (const c of this.cores) if (Number.isNaN(c.cyclePs)) c.cyclePs = this.defaultCycle(c);

    // Records are annotated in place: the reader's objects are ours, and copying a
    // quarter of a million of them is mostly garbage collection.
    this.dispatches = data.dispatches.map((d, i) => {
      const core = this.cores[d.core];
      d.i = i; d.kind = d.kind || "run"; d.coreKey = core.key; d.chip = core.chip; d.ci = core.ci;
      d.open = d.t1 == null; d.t0 = d.t0 == null ? 0 : d.t0; d.t1 = d.t1 == null ? NaN : d.t1;
      core.dispatches.push(d);
      return d;
    });

    // A wait's and a flag write's flag id, and a write's stamp, live in arrays beside
    // the records rather than on them: links.js groups and binary-searches a million
    // of them, and reading a column beats reading a field off a million objects. These
    // two passes are also where the SparseCores that showed up are collected, as one
    // packed integer each (a SparseCore index is well under 1024): a slice has a
    // million waits and writes and walking them again for that costs more than it says.
    const scPairs = new Set();
    this.waits = data.waits;
    this.waitKey = new Float64Array(this.waits.length);
    // A column-backed wait answers `open`, `t0`, `t1` and `stallPs` off its columns
    // already (pack.js); one from a JSONL build or an older pack carries the raw
    // fields and is normalised here.
    const rawWaits = this.waits.length > 0 && Object.hasOwn(this.waits[0], "t0");
    for (let i = 0; i < this.waits.length; i++) {
      const w = this.waits[i];
      const chip = w.chip, core = w.core, ci = w.ci;
      w.i = i;
      this.waitKey[i] = flagIdOf(chip, core, ci, w.tile, w.idx);
      if (rawWaits) {
        w.open = w.t1 == null; w.t0 = w.t0 == null ? 0 : w.t0; w.t1 = w.t1 == null ? NaN : w.t1;
        w.stallPs = w.stall_ps == null ? 0 : w.stall_ps;
      }
      if (core === "SparseCore" && ci != null) scPairs.add(chip * 1024 + ci);
    }
    // The non-flag stalls (C2C, tile barriers, done spins): shaped like waits.
    this.stalls = (data.stalls || []).map((s, i) => {
      // `core` becomes the core object; the index it was is kept so the same data
      // can be modelled again (a gen or shape override rebuilds the model in place).
      if (s.coreIndex == null) s.coreIndex = s.core;
      const core = this.cores[s.coreIndex];
      s.i = i; s.core = core; s.chip = core.chip;
      s.open = s.t1 == null; s.t0 = s.t0 == null ? (s.t1 == null ? 0 : s.t1 - (s.stall_ps || 0)) : s.t0;
      s.t1 = s.t1 == null ? NaN : s.t1; s.stallPs = s.stall_ps == null ? 0 : s.stall_ps;
      return s;
    });
    this.flags = data.flags;
    this.flagKey = new Float64Array(this.flags.length);
    this.flagAt = new Float64Array(this.flags.length);
    for (let i = 0; i < this.flags.length; i++) {
      const f = this.flags[i];
      const chip = f.chip, core = f.core, ci = f.ci;
      f.i = i; if (f.at == null) f.at = 0;
      this.flagKey[i] = flagIdOf(chip, core, ci, f.tile, f.idx);
      this.flagAt[i] = f.at;
      if (core === "SparseCore" && ci != null) scPairs.add(chip * 1024 + ci);
    }
    this.dmas = data.dmas.map((d, i) => {
      const by = parseBy(d.by);
      // Which chips a descriptor touched. Columns say it without building the runs
      // (pack.js); records from a JSONL trace or an older pack are asked for them.
      if (d.srcChips === undefined) {
        const srcChips = new Set(), dstChips = new Set();
        for (const r of d.runs) {
          if (r.src && r.src.chip != null) srcChips.add(r.src.chip);
          if (r.dst && r.dst.chip != null) dstChips.add(r.dst.chip);
        }
        d.srcChips = srcChips; d.dstChips = dstChips;
        d.cross = d.runs.some((r) => (r.src || {}).chip !== (r.dst || {}).chip);
      }
      d.i = i; d.byKind = by.kind; d.byCi = by.ci;
      d.open = d.end == null; d.orphan = d.at == null;
      d.at = d.at == null ? NaN : d.at; d.start = d.start == null ? NaN : d.start; d.end = d.end == null ? NaN : d.end;
      return d;
    });
    this.dmaById = new Map(this.dmas.map((d) => [d.id, d]));

    // Host: fw_cmd/fw_done pairs by (chip, id); the rest are ticks.
    this.host = data.host.map((h, i) => { h.i = i; h.ps = NaN; h.placed = 2; return h; });
    this.hostByChip = new Map();
    this.fwByCmd = new Map();
    for (const h of this.host) {
      if (!this.hostByChip.has(h.chip)) this.hostByChip.set(h.chip, []);
      this.hostByChip.get(h.chip).push(h);
      if (h.ev === "fw_cmd") this.fwByCmd.set(h.chip + ":" + h.id, h);
    }
    for (const h of this.host) {
      if (h.ev === "fw_done") {
        const c = this.fwByCmd.get(h.chip + ":" + h.id);
        if (c && !c.done) { c.done = h; h.cmd = c; }
      }
    }

    // SparseCores: the ones that left any trace, streams or not.
    this.scSeen = new Map();
    const seeSc = (chip, ci) => {
      if (ci == null) return;
      if (!this.scSeen.has(chip)) this.scSeen.set(chip, new Set());
      this.scSeen.get(chip).add(ci);
    };
    for (const p of scPairs) seeSc(Math.floor(p / 1024), p % 1024);
    for (const c of this.cores) if (c.kind !== "tc") seeSc(c.chip, c.ci);
    for (const d of this.dmas) if (d.byKind === "sc") seeSc(d.chip, d.byCi);
    for (const h of this.host) {
      if (h.ev === "fw_cmd" && h.kind === "run" && h.core === "SparseCore") seeSc(h.chip, h.ci);
    }

    // When each chip's last thing happened, and where the cores were busy at all. Chip
    // numbers are small and dense, so the running maxima are an array: the walk is over
    // every record in the run. NaN loses every comparison, which is what `open` wants.
    const nChip = this.chipIds[this.chipIds.length - 1] + 1;
    const tend = new Float64Array(nChip);
    const bump = (chip, t) => { if (chip >= 0 && chip < nChip && t > tend[chip]) tend[chip] = t; };
    for (const c of this.cores) bump(c.chip, c.tLast);
    for (const d of this.dispatches) bump(d.chip, d.t1);
    for (const w of this.waits) bump(w.chip, w.t1);
    for (const f of this.flags) bump(f.chip, f.at);
    for (const d of this.dmas) bump(d.chip, d.end);
    this.chipTend = new Map(this.chipIds.map((chip) => [chip, tend[chip]]));
    let a = Infinity, b = 0;
    for (const c of this.cores) {
      if (!Number.isNaN(c.tFirst)) a = Math.min(a, c.tFirst);
      if (!Number.isNaN(c.tLast)) b = Math.max(b, c.tLast);
    }
    for (const d of this.dispatches) { a = Math.min(a, d.t0); if (!d.open) b = Math.max(b, d.t1); }
    for (const w of this.waits) if (!w.open) b = Math.max(b, w.t1);
    if (!Number.isFinite(a) || a >= b) { a = 0; b = this.tEnd; }
    this.spanFull = [a, Math.max(b, a + 1)];
    // The default view is where most of the bundles are: every stream's issues,
    // sampled and sorted, trimmed of the sparsest 5% at each end. A core dispatched
    // early and left waiting for the host does not stretch it; chips working one
    // after another all fall inside it. When that is most of the run anyway, the
    // view is the run.
    let total = 0;
    for (const c of this.cores) total += c.n;
    if (total > 0) {
      const stride = Math.max(1, Math.floor(total / 200000));
      const buf = new Float64Array(Math.ceil(total / stride) + this.cores.length);
      let ns = 0;
      for (const c of this.cores) for (let i = 0; i < c.n; i += stride) if (!Number.isNaN(c.t[i])) buf[ns++] = c.t[i];
      const sample = buf.subarray(0, ns);
      sample.sort();                             // numeric, and without a comparator call per pair
      const at = (q) => sample[Math.min(ns - 1, Math.floor(ns * q))];
      // The strips and the minimap: where 98% of the issues are, padded. A chip that
      // works while the others wait stays inside it (its bundles are a share of the
      // 98%); the host's bring-up before the first bundle and a long tail of nothing
      // after the last are out.
      const s0 = at(0.01), s1 = at(0.99);
      if (s1 > s0) { const pad = (s1 - s0) * 0.02; this.spanTrim = [Math.max(a, s0 - pad), Math.min(b, s1 + pad)]; }
      const lo = at(0.05), hi = at(0.95);
      if (hi > lo && (hi - lo) < 0.5 * (this.spanFull[1] - this.spanFull[0])) { a = lo; b = hi; }
    }
    // Where the timeline opens (`focus`), and what the overview strips and the
    // minimap span (`span`).
    this.focus = [a, Math.max(b, a + 1)];
    this.span = this.spanTrim || this.spanFull;
  }

  // --- topology ----------------------------------------------------------------

  setTopology(gen, shape) {
    const inferred = { gen: false, shape: false };
    if (!GENS.includes(gen)) {
      gen = this.inferGen();
      inferred.gen = true;
    }
    let chips = this.chipsSeen.length ? Math.max(...this.chipsSeen) + 1 : 1;
    if (!shape || shape.length !== 3) {
      const hit = MENU.find(([g, s]) => g === gen && s[0] * s[1] * s[2] === chips);
      shape = hit ? hit[1].slice() : [chips, 1, 1];
      inferred.shape = true;
    }
    this.gen = gen;
    this.shape = shape;
    this.inferred = inferred;
    this.tcPerChip = Math.max(TC_PER_CHIP[gen] || 1, ...this.cores.filter((c) => c.kind === "tc").map((c) => c.ci + 1));
    this.scPerChip = Math.max(SC_PER_CHIP[gen] || 0, ...this.cores.filter((c) => c.kind !== "tc").map((c) => c.ci + 1));
    const n = shape[0] * shape[1] * shape[2];
    const ids = new Set(this.chipsSeen);
    for (let i = 0; i < n; i++) ids.add(i);
    this.chipIds = [...ids].sort((a, b) => a - b);
  }

  inferGen() {
    const tcs = Math.max(0, ...this.cores.filter((c) => c.kind === "tc").map((c) => c.ci + 1));
    let hasSc = this.cores.some((c) => c.kind !== "tc");
    for (const w of this.data.waits) if (w.core === "SparseCore") hasSc = true;
    for (const f of this.data.flags) if (f.core === "SparseCore") hasSc = true;
    let v7 = !!this.data.v7Slots;   // the worker read the slot names it kept
    for (const c of this.cores) {
      for (const u of c.units) if (u.unit === "mxu" && (u.op === "push_upper" || u.op === "operand_upper")) v7 = true;
      if (c.kind !== "tc") continue;
      for (let j = 0; j < c.slotUnit.length && !v7; j++) {
        const s = this.strings[c.slotUnit[j]];
        if (s === "misc" || s === "vst0" || s === "vst1") v7 = true;
      }
    }
    if (v7) return "v7x";
    if (tcs <= 1 && !hasSc) return "v5e";
    return "v5p";
  }

  coords(chip) {
    const [X, Y] = this.shape;
    return [chip % X, Math.floor(chip / X) % Y, Math.floor(chip / (X * Y))];
  }

  defaultCycle(core) {
    if (core && core.kind !== "tc") return this.scCyclePs();
    return 1e12 / (TC_HZ[this.gen] || 1.5e9);
  }

  scCyclePs() {
    return 1e12 / (SC_HZ[this.gen] || 1.5e9);
  }

  // --- cores ---------------------------------------------------------------------

  buildCore(c, idx) {
    const n = c.step.length;
    // Which units each bundle used: the mask the wasm reader built, or a walk of the columns.
    let umask = c.umask;
    if (!umask) {
      umask = new Uint16Array(n);
      if (c.umaskRaw) {
        // The wasm reader's per-issue mask, in its own bit order, mapped to ours.
        const map = (c.unitNames || []).map((name) => UNIT_BIT[unitOfSlot(name)] || UNIT_BIT.misc);
        for (let i = 0; i < n; i++) {
          let m = 0, raw = c.umaskRaw[i];
          for (let b = 0; raw; b++, raw >>>= 1) if (raw & 1) m |= map[b] || UNIT_BIT.misc;
          umask[i] = m;
        }
      } else {
        for (let i = 0; i < n; i++) {
          let m = 0;
          for (let j = c.slotOff[i]; j < c.slotOff[i + 1]; j++) m |= this.slotBitOf(c.slotUnit[j]);
          umask[i] = m;
        }
      }
    }
    // Unit events (older traces): attach to the bundle at their step; refine the mask.
    const unitBi = new Int32Array(c.units.length);
    for (let k = 0; k < c.units.length; k++) {
      const u = c.units[k];
      const bi = lowerIndex(c.step, u.step, n);
      if (bi >= 0 && c.step[bi] === u.step) {
        umask[bi] |= UNIT_EV_BIT[u.unit] || 0;
        unitBi[k] = bi;
      } else {
        unitBi[k] = -1;
      }
      u.bi = unitBi[k];
    }
    // The period the reader measured where it dropped the cycle counts, else the same
    // measurement off the columns themselves.
    let cyclePs = c.cyclePs == null ? NaN : c.cyclePs;
    if (Number.isNaN(cyclePs) && c.cyc) {
      for (let i = n - 1; i >= 0 && Number.isNaN(cyclePs); i--) {
        if (!Number.isNaN(c.t[i]) && c.cyc[i] > 0) cyclePs = c.t[i] / c.cyc[i];
      }
    }
    const tFirst = n ? c.t[0] : NaN, tLast = n ? c.t[n - 1] : NaN;
    const kind = c.kind || "tc";
    const tile = c.tile == null ? null : c.tile;
    const short = kind === "tc" ? `TC${c.ci}` : kind === "sc" ? `SC${c.ci}` : `SC${c.ci} tile ${tile}`;
    return {
      ...c, kind, tile, idx, n, umask, unitBi, cyclePs, tFirst, tLast, dispatches: [],
      short, label: `chip ${c.chip} ${short}`, rowId: c.key,
      // Whether the columns carry the bundles' text; otherwise it is rendered on demand.
      hasText: c.slotOff.length > 0 && c.dis.length === n,
    };
  }

  // The bundles' text and the string table, if the worker ever hands them over.
  // Everything below that reads a slot reads them.
  applyListing(text) {
    for (const c of this.cores) {
      const t = text.cores[c.key];
      if (!t) continue;
      c.dis = t.dis; c.slotOff = t.slotOff; c.slotUnit = t.slotUnit; c.slotIns = t.slotIns;
      c.hasText = c.slotOff.length > 0 && c.dis.length === c.n;
    }
    this.strings = text.strings || decodeStrings(text.strBlob, text.strOff);
    this.slotBit = new Uint16Array(this.strings.length);
  }

  slotBitOf(sid) {
    let b = this.slotBit[sid];
    if (!b) {
      b = UNIT_BIT[unitOfSlot(this.strings[sid])] || UNIT_BIT.misc;
      this.slotBit[sid] = b;
    }
    return b;
  }

  // Index of the last bundle issued at or before `t`, or -1.
  bundleAt(core, t) {
    return lowerIndex(core.t, t, core.n);
  }

  // A bundle's cycle count: the column, where the run kept one, else the stamp over
  // the stream's period, which is what the column held.
  cycAt(core, bi) {
    return core.cyc ? core.cyc[bi] : Math.round(core.t[bi] / core.cyclePs);
  }

  bundleByStep(core, step) {
    const i = lowerIndex(core.step, step, core.n);
    return i >= 0 && core.step[i] === step ? i : -1;
  }

  unitsAtBundle(core, bi) {
    const lo = upperIndex(core.unitBi, bi);
    const out = [];
    for (let k = lo; k < core.units.length && core.unitBi[k] === bi; k++) out.push(core.units[k]);
    return out;
  }

  regsAtStep(core, step) {
    const i = lowerIndex(core.regStep, step);
    return i >= 0 && core.regStep[i] === step ? this.strings[core.regD[i]] : null;
  }

  slotsOf(core, bi) {
    const out = [];
    for (let j = core.slotOff[bi]; j < core.slotOff[bi + 1]; j++) {
      out.push({ u: this.strings[core.slotUnit[j]], i: this.strings[core.slotIns[j]] });
    }
    return out;
  }

  // A bundle's text: the listing line for a TensorCore; for a SparseCore stream the
  // slots joined, since its `dis` is the decoder's own debug print.
  disOf(core, bi) {
    if (core.kind === "tc") return this.strings[core.dis[bi]];
    return this.slotsOf(core, bi).map((s) => `${s.u}: ${s.i}`).join("   ");
  }

  // The nop run covering `step` on this core, if any: [index, start step, n].
  nopAtStep(core, step) {
    const i = lowerIndex(core.nopStep, step);
    if (i < 0) return null;
    const s = core.nopStep[i], n = core.nopN[i];
    return step < s + n ? { i, step: s, pc: core.nopPc[i], n } : null;
  }

  // Per pc, the bundle index with the most slots (the listing's variant), lazily.
  pcVariants(core) {
    if (!core._pcVar) {
      const m = new Map();
      for (let i = 0; i < core.n; i++) {
        const pc = core.pc[i];
        const prev = m.get(pc);
        const width = (k) => core.slotN ? core.slotN[k] : core.hasText ? core.slotOff[k + 1] - core.slotOff[k] : popcount16(core.umask[k]);
        if (prev === undefined || width(i) > width(prev)) m.set(pc, i);
      }
      core._pcVar = m;
    }
    return core._pcVar;
  }

  // Bundle indexes that executed `pc`, ascending, lazily.
  pcOccurrences(core, pc) {
    if (!core._pcOcc) {
      const m = new Map();
      for (let i = 0; i < core.n; i++) {
        const p = core.pc[i];
        let a = m.get(p);
        if (!a) { a = []; m.set(p, a); }
        a.push(i);
      }
      core._pcOcc = m;
    }
    return core._pcOcc.get(pc) || [];
  }

  chipLabel(chip) {
    const [x, y, z] = this.coords(chip);
    return this.shape[2] > 1 ? `chip ${chip} (${x},${y},${z})` : `chip ${chip} (${x},${y})`;
  }

  // The TensorCores of a chip that ran a program.
  coresOfChip(chip) {
    return this.cores.filter((c) => c.chip === chip && c.kind === "tc").sort((a, b) => a.ci - b.ci);
  }

  // The SparseCore sequencer streams of a chip.
  scCoresOfChip(chip) {
    return this.cores.filter((c) => c.chip === chip && c.kind === "sc").sort((a, b) => a.ci - b.ci);
  }

  // The tile streams of one SparseCore.
  tilesOf(chip, ci) {
    return this.cores.filter((c) => c.chip === chip && c.kind === "tile" && c.ci === ci).sort((a, b) => a.tile - b.tile);
  }

  // Every bundle stream of a chip, TensorCores first.
  streamsOfChip(chip) {
    const order = { tc: 0, sc: 1, tile: 2 };
    return this.cores.filter((c) => c.chip === chip).sort((a, b) => order[a.kind] - order[b.kind] || a.ci - b.ci || (a.tile ?? -1) - (b.tile ?? -1));
  }

  scsOfChip(chip) {
    return [...(this.scSeen.get(chip) || [])].sort((a, b) => a - b);
  }
}
